import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Upload, User, MapPin, Clock, DollarSign, Package, 
  Search, Filter, Eye, ChevronDown, ChevronUp, 
  FileSpreadsheet, Shield, AlertTriangle, CheckCircle,
  RefreshCw, Download, TrendingUp, BarChart3, PieChart as PieChartIcon, Link2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// Chart colors
const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16'];

// Custom tooltip for charts - defined outside component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg">
        <p className="font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Pricing config display component - defined outside
const PricingConfigDisplay = ({ config }) => {
  if (!config) return <span className="text-muted-foreground">-</span>;
  
  return (
    <div className="space-y-1 text-xs">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs">
          {config.price_type || 'NET'}
        </Badge>
        {config.commission_rate > 0 && (
          <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
            %{config.commission_rate} Kom.
          </Badge>
        )}
      </div>
      <div className="text-muted-foreground">
        Kargo: {config.shipping_included ? 'Dahil' : 'Hariç'} 
        ({config.shipping_handler === 'turkishemarket' ? 'TM' : 'Satıcı'})
      </div>
      <div className="text-muted-foreground">
        KDV: {config.vat_included ? 'Dahil' : `Hariç %${config.vat_rate || 0}`}
      </div>
    </div>
  );
};

// Session detail dialog component - defined outside
const SessionDetailDialog = ({ session, formatDate }) => (
  <Dialog>
    <DialogTrigger asChild>
      <Button variant="ghost" size="sm">
        <Eye className="h-4 w-4" />
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          {session.first_name} {session.last_name}
        </DialogTitle>
        <DialogDescription>{session.seller_email}</DialogDescription>
      </DialogHeader>
      
      <div className="grid grid-cols-2 gap-4 mt-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4 text-red-500" />
              IP Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="font-mono text-lg">{session.ip_address || 'Bilinmiyor'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Kayıt: {formatDate(session.created_at)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-500" />
              Onay Durumu
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="flex items-center gap-2">
              {session.consent_given ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
              <span>{session.consent_given ? 'Onaylandı' : 'Onay Bekleniyor'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-blue-500" />
            Fiyatlandırma Ayarları
          </CardTitle>
        </CardHeader>
        <CardContent>
          {session.pricing_config ? (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Fiyat Türü:</span>
                <span className="ml-2 font-medium">{session.pricing_config.price_type}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Komisyon:</span>
                <span className="ml-2 font-medium">%{session.pricing_config.commission_rate || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Kargo Dahil:</span>
                <span className="ml-2 font-medium">{session.pricing_config.shipping_included ? 'Evet' : 'Hayır'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Kargo Sorumlusu:</span>
                <span className="ml-2 font-medium">
                  {session.pricing_config.shipping_handler === 'turkishemarket' ? 'Turkishemarket.com' : 'Satıcı'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">KDV:</span>
                <span className="ml-2 font-medium">
                  {session.pricing_config.vat_included ? 'Dahil' : `Hariç %${session.pricing_config.vat_rate || 0}`}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">Fiyatlandırma ayarı yapılmamış</p>
          )}
        </CardContent>
      </Card>
    </DialogContent>
  </Dialog>
);

const SellerUploadsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('analytics');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionsRes, logsRes] = await Promise.all([
        api.get('/seller/product-chat/admin/sessions'),
        api.get('/seller/product-chat/admin/upload-logs')
      ]);
      setSessions(sessionsRes.data.sessions || []);
      setUploadLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (dataType = 'all') => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/seller/product-chat/admin/export-excel?data_type=${dataType}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'satici_yuklemeleri.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Excel dosyası indirildi!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Excel export başarısız oldu');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const filteredSessions = sessions.filter(session => 
    session.seller_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.ip_address?.includes(searchTerm)
  );

  const filteredLogs = uploadLogs.filter(log =>
    log.seller_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.seller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.file_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Analytics calculations
  const analytics = useMemo(() => {
    // Price type distribution
    const priceTypeData = sessions.reduce((acc, session) => {
      const type = session.pricing_config?.price_type || 'Belirtilmemiş';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Shipping handler distribution
    const shippingData = sessions.reduce((acc, session) => {
      const handler = session.pricing_config?.shipping_handler === 'turkishemarket' 
        ? 'TurkishEmarket' 
        : session.pricing_config?.shipping_handler === 'seller' 
          ? 'Satıcı' 
          : 'Belirtilmemiş';
      acc[handler] = (acc[handler] || 0) + 1;
      return acc;
    }, {});

    // Upload source type (file vs URL)
    const sourceTypeData = uploadLogs.reduce((acc, log) => {
      const source = log.source_type === 'url' ? 'URL/Link' : 'Dosya';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});

    // Commission rate distribution
    const commissionData = sessions.reduce((acc, session) => {
      const rate = session.pricing_config?.commission_rate || 0;
      const range = rate === 0 ? '0%' : rate <= 5 ? '1-5%' : rate <= 10 ? '6-10%' : rate <= 15 ? '11-15%' : rate <= 20 ? '16-20%' : '20%+';
      acc[range] = (acc[range] || 0) + 1;
      return acc;
    }, {});

    // Daily uploads trend (last 7 days)
    const today = new Date();
    const dailyUploads = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('tr-TR', { weekday: 'short' });
      
      const count = uploadLogs.filter(log => {
        const logDate = new Date(log.timestamp).toISOString().split('T')[0];
        return logDate === dateStr;
      }).length;

      const products = uploadLogs
        .filter(log => {
          const logDate = new Date(log.timestamp).toISOString().split('T')[0];
          return logDate === dateStr;
        })
        .reduce((sum, log) => sum + (log.products_count || 0), 0);

      dailyUploads.push({ 
        name: dayName, 
        yükleme: count, 
        ürün: products,
        date: dateStr 
      });
    }

    // Products by seller (top 5)
    const sellerProducts = uploadLogs.reduce((acc, log) => {
      const name = log.seller_name || 'Bilinmiyor';
      acc[name] = (acc[name] || 0) + (log.products_count || 0);
      return acc;
    }, {});

    const topSellers = Object.entries(sellerProducts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name: name.substring(0, 15), ürün: value }));

    // Session state distribution
    const stateData = sessions.reduce((acc, session) => {
      const state = session.state === 'ready' ? 'Hazır' 
        : session.state === 'pricing' ? 'Fiyatlandırma' 
        : session.state === 'disclaimer' ? 'Onay Bekliyor'
        : session.state || 'Bilinmiyor';
      acc[state] = (acc[state] || 0) + 1;
      return acc;
    }, {});

    return {
      priceTypeData: Object.entries(priceTypeData).map(([name, value]) => ({ name, value })),
      shippingData: Object.entries(shippingData).map(([name, value]) => ({ name, value })),
      sourceTypeData: Object.entries(sourceTypeData).map(([name, value]) => ({ name, value })),
      commissionData: Object.entries(commissionData).map(([name, value]) => ({ name, value })),
      dailyUploads,
      topSellers,
      stateData: Object.entries(stateData).map(([name, value]) => ({ name, value })),
      totalProducts: uploadLogs.reduce((sum, log) => sum + (log.products_count || 0), 0),
      totalSessions: sessions.length,
      consentRate: sessions.length > 0 
        ? Math.round((sessions.filter(s => s.consent_given).length / sessions.length) * 100) 
        : 0,
      avgCommission: sessions.length > 0
        ? (sessions.reduce((sum, s) => sum + (s.pricing_config?.commission_rate || 0), 0) / sessions.length).toFixed(1)
        : 0
    };
  }, [sessions, uploadLogs]);

  const getStateColor = (state) => {
    switch (state) {
      case 'ready': return 'bg-green-100 text-green-800';
      case 'pricing': return 'bg-blue-100 text-blue-800';
      case 'disclaimer': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStateLabel = (state) => {
    switch (state) {
      case 'ready': return 'Hazır';
      case 'pricing': return 'Fiyatlandırma';
      case 'disclaimer': return 'Onay Bekliyor';
      default: return state;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="seller-uploads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="h-6 w-6 text-purple-600" />
            Satıcı Yüklemeleri
          </h1>
          <p className="text-muted-foreground">
            Satıcı oturumları, IP adresleri, fiyatlandırma ve analitik
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => handleExportExcel('all')} 
            variant="default" 
            className="gap-2 bg-green-600 hover:bg-green-700"
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            {exporting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Excel İndir
          </Button>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-purple-100">
                <User className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{analytics.totalSessions}</p>
                <p className="text-sm text-muted-foreground">Toplam Oturum</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">%{analytics.consentRate}</p>
                <p className="text-sm text-muted-foreground">Onay Oranı</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{analytics.totalProducts}</p>
                <p className="text-sm text-muted-foreground">Toplam Ürün</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-100">
                <DollarSign className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">%{analytics.avgCommission}</p>
                <p className="text-sm text-muted-foreground">Ort. Komisyon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="analytics" className="gap-2" data-testid="analytics-tab">
            <BarChart3 className="h-4 w-4" />
            Analitik
          </TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2" data-testid="sessions-tab">
            <User className="h-4 w-4" />
            Oturumlar ({sessions.length})
          </TabsTrigger>
          <TabsTrigger value="uploads" className="gap-2" data-testid="uploads-tab">
            <Upload className="h-4 w-4" />
            Yüklemeler ({uploadLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Row 1: Daily Trend + Top Sellers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  Son 7 Gün Yükleme Trendi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={analytics.dailyUploads}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="yükleme" 
                      stroke="#8b5cf6" 
                      fill="#8b5cf6" 
                      fillOpacity={0.3}
                      name="Yükleme"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="ürün" 
                      stroke="#10b981" 
                      fill="#10b981" 
                      fillOpacity={0.3}
                      name="Ürün"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="h-5 w-5 text-green-500" />
                  En Çok Ürün Yükleyen Satıcılar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.topSellers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="ürün" fill="#10b981" radius={[0, 4, 4, 0]} name="Ürün Sayısı" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Pie Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-purple-500" />
                  Fiyat Türü Dağılımı
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.priceTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {analytics.priceTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="h-4 w-4 text-blue-500" />
                  Kargo Sorumlusu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.shippingData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {analytics.shippingData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-amber-500" />
                  Yükleme Kaynağı
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.sourceTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {analytics.sourceTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-500" />
                  Oturum Durumu
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.stateData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {analytics.stateData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Commission Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-amber-500" />
                Komisyon Oranı Dağılımı
              </CardTitle>
              <CardDescription>Satıcıların belirlediği komisyon oranları</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.commissionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Satıcı Sayısı" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions">
          {/* Search */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Satıcı adı, e-posta veya IP ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Satıcı</TableHead>
                    <TableHead>IP Adresi</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Onay</TableHead>
                    <TableHead>Fiyatlandırma</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead className="w-16">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Henüz oturum kaydı yok
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSessions.map((session) => (
                      <TableRow key={session.session_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {session.first_name} {session.last_name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {session.seller_email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {session.ip_address || '-'}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStateColor(session.state)}>
                            {getStateLabel(session.state)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {session.consent_given ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          <PricingConfigDisplay config={session.pricing_config} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(session.created_at)}
                        </TableCell>
                        <TableCell>
                          <SessionDetailDialog session={session} formatDate={formatDate} />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Uploads Tab */}
        <TabsContent value="uploads">
          {/* Search */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Satıcı adı, e-posta veya dosya adı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Satıcı</TableHead>
                    <TableHead>Kaynak</TableHead>
                    <TableHead>Ürün Sayısı</TableHead>
                    <TableHead>Engellenen</TableHead>
                    <TableHead>IP Adresi</TableHead>
                    <TableHead>Fiyatlandırma</TableHead>
                    <TableHead>Tarih</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Henüz yükleme kaydı yok
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLogs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{log.seller_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {log.seller_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {log.source_type === 'url' ? (
                              <Link2 className="h-4 w-4 text-amber-500" />
                            ) : (
                              <FileSpreadsheet className="h-4 w-4 text-blue-500" />
                            )}
                            <span className="text-sm">
                              {log.source_type === 'url' 
                                ? (log.source_url?.substring(0, 30) + '...' || 'URL')
                                : log.file_name
                              }
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            {log.products_count} ürün
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.prohibited_blocked > 0 ? (
                            <Badge variant="destructive">
                              {log.prohibited_blocked} engellendi
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {log.ip_address || '-'}
                          </code>
                        </TableCell>
                        <TableCell>
                          <PricingConfigDisplay config={log.pricing_config} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(log.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SellerUploadsPage;
